
import React, { useState } from "react";
import { reportsService, tasksService } from "@/services";
import { useAuth } from "@/lib/AuthContext";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from "recharts";
import {
  LayoutDashboard,
  TrendingUp,
  AlertCircle,
  CheckCircle2,
  Clock,
  MapPin,
  Users,
  Activity
} from "lucide-react";
import { format, subDays, differenceInDays } from "date-fns";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";


const COLORS = {
  reported: '#ef4444',
  acknowledged: '#eab308',
  in_progress: '#3b82f6',
  resolved: '#22c55e',
  closed: '#6b7280'
};

const statusColors = {
  reported: { bg: "bg-red-100", text: "text-red-700", icon: AlertCircle },
  acknowledged: { bg: "bg-yellow-100", text: "text-yellow-700", icon: Clock },
  in_progress: { bg: "bg-blue-100", text: "text-blue-700", icon: TrendingUp },
  waiting_contractor: { bg: "bg-orange-100", text: "text-orange-700", icon: Clock },
  resolved: { bg: "bg-green-100", text: "text-green-700", icon: CheckCircle2 },
  closed: { bg: "bg-gray-100", text: "text-gray-700", icon: CheckCircle2 },
};

export default function CityDashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [assigningReport, setAssigningReport] = React.useState(null);

  const [contractorEmail, setContractorEmail] = React.useState("");
  const [viewMode, setViewMode] = React.useState("analytics");

  const { data: reports = [], isLoading } = useQuery({
    queryKey: ['allReports'],
    queryFn: () => reportsService.list('-created_date'),
    initialData: [],
  });

  const assignContractorMutation = useMutation({
    mutationFn: async ({ reportId, email, paymentAmount }) => {
      if (!email || !email.includes('@')) {
        throw new Error("Invalid email address");
      }

      const report = reports.find(r => r.id === reportId);
      await tasksService.create({
        report_id: reportId,
        contractor_email: email,
        payment_amount: paymentAmount || 300,
        // status defaults to 'assigned' in database
      });

      await reportsService.update(reportId, {
        status: 'acknowledged',
        assigned_to: email,
      });
      
      return { email, title: report?.title };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['allReports'] });
      toast.success(`✅ Contractor ${data.email} assigned to "${data.title}"`, {
        duration: 4000,
      });
      setAssigningReport(null);
      setContractorEmail("");
    },
    onError: (error) => {
      if (error.message === "Invalid email address") {
        toast.error("Please enter a valid contractor email address");
      } else {
        toast.error("Failed to assign contractor. Please try again.");
      }
    },
  });

  // Calculate stats
  const stats = {
    total: reports.length,
    reported: reports.filter(r => r.status === 'reported').length,
    acknowledged: reports.filter(r => r.status === 'acknowledged').length,
    in_progress: reports.filter(r => r.status === 'in_progress').length,
    resolved: reports.filter(r => r.status === 'resolved').length,
  };

  // Status distribution for pie chart
  const statusData = [
    { name: 'Reported', value: stats.reported, color: COLORS.reported },
    { name: 'Acknowledged', value: stats.acknowledged, color: COLORS.acknowledged },
    { name: 'In Progress', value: stats.in_progress, color: COLORS.in_progress },
    { name: 'Resolved', value: stats.resolved, color: COLORS.resolved },
  ].filter(item => item.value > 0);

  // Category distribution
  const categoryStats = reports.reduce((acc, report) => {
    acc[report.category] = (acc[report.category] || 0) + 1;
    return acc;
  }, {});

  const categoryData = Object.entries(categoryStats).map(([category, count]) => ({
    category: category.replace('_', ' '),
    count
  })).sort((a, b) => b.count - a.count);

  // Reports over time (last 7 days)
  const last7Days = Array.from({ length: 7 }, (_, i) => {
    const date = subDays(new Date(), 6 - i);
    return {
      date: format(date, 'MMM d'),
      count: reports.filter(r => 
        format(new Date(r.created_date), 'MMM d') === format(date, 'MMM d')
      ).length
    };
  });

  // Top neighborhoods
  const neighborhoodStats = reports.reduce((acc, report) => {
    const neighborhood = report.neighborhood || 'Unknown';
    acc[neighborhood] = (acc[neighborhood] || 0) + 1;
    return acc;
  }, {});

  const topNeighborhoods = Object.entries(neighborhoodStats)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  // Average resolution time
  const resolvedReports = reports.filter(r => r.status === 'resolved' && r.resolved_date);
  const avgResolutionTime = resolvedReports.length > 0
    ? Math.round(
        resolvedReports.reduce((acc, r) => 
          acc + differenceInDays(new Date(r.resolved_date), new Date(r.created_date)), 0
        ) / resolvedReports.length
      )
    : 0;

  // High priority reports (multiple upvotes)
  const highPriorityReports = reports
    .filter(r => r.status !== 'resolved' && r.status !== 'closed' && (r.upvote_count || 0) >= 3)
    .sort((a, b) => (b.upvote_count || 0) - (a.upvote_count || 0))
    .slice(0, 5);

  // Redirect non-city-workers to MapView (role refresh will fix this within 30s)
  if (!user || user.user_role !== 'city_worker') {
    navigate(createPageUrl("MapView"));
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 p-4 md:p-8 overflow-y-auto">
      <div className="max-w-7xl mx-auto pb-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <LayoutDashboard className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">City Dashboard</h1>
            </div>
            <div className="flex gap-2">
              <Button
                variant={viewMode === "analytics" ? "default" : "outline"}
                onClick={() => setViewMode("analytics")}
                size="sm"
              >
                Analytics
              </Button>
              <Button
                variant={viewMode === "table" ? "default" : "outline"}
                onClick={() => setViewMode("table")}
                size="sm"
              >
                All Reports
              </Button>
            </div>
          </div>
          <p className="text-gray-600 dark:text-gray-300">
            Monitor and manage community infrastructure reports
          </p>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card className="p-5 bg-gradient-to-br from-gray-50 to-white dark:from-gray-800 dark:to-gray-700">
            <div className="flex items-center gap-3 mb-2">
              <Activity className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              <span className="text-sm text-gray-600 dark:text-gray-300">Total Reports</span>
            </div>
            <p className="text-3xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
          </Card>

          <Card className="p-5 bg-gradient-to-br from-red-50 to-white dark:from-red-900/20 dark:to-gray-800 border-red-200 dark:border-red-800">
            <div className="flex items-center gap-3 mb-2">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
              <span className="text-sm text-red-600 dark:text-red-400">Reported</span>
            </div>
            <p className="text-3xl font-bold text-red-700 dark:text-red-300">{stats.reported}</p>
          </Card>

          <Card className="p-5 bg-gradient-to-br from-yellow-50 to-white dark:from-yellow-900/20 dark:to-gray-800 border-yellow-200 dark:border-yellow-800">
            <div className="flex items-center gap-3 mb-2">
              <Clock className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
              <span className="text-sm text-yellow-600 dark:text-yellow-400">Acknowledged</span>
            </div>
            <p className="text-3xl font-bold text-yellow-700 dark:text-yellow-300">{stats.acknowledged}</p>
          </Card>

          <Card className="p-5 bg-gradient-to-br from-blue-50 to-white dark:from-blue-900/20 dark:to-gray-800 border-blue-200 dark:border-blue-800">
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <span className="text-sm text-blue-600 dark:text-blue-400">In Progress</span>
            </div>
            <p className="text-3xl font-bold text-blue-700 dark:text-blue-300">{stats.in_progress}</p>
          </Card>

          <Card className="p-5 bg-gradient-to-br from-green-50 to-white dark:from-green-900/20 dark:to-gray-800 border-green-200 dark:border-green-800">
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
              <span className="text-sm text-green-600 dark:text-green-400">Resolved</span>
            </div>
            <p className="text-3xl font-bold text-green-700 dark:text-green-300">{stats.resolved}</p>
          </Card>
        </div>

        {viewMode === "analytics" ? (
          <>
            {/* Charts Row */}
            <div className="grid lg:grid-cols-2 gap-6 mb-6">
              {/* Status Distribution */}
              <Card className="p-6 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm">
                <h3 className="font-bold text-lg mb-4 text-gray-900 dark:text-white">Status Distribution</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={statusData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {statusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Card>

              {/* Reports Trend */}
              <Card className="p-6 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm">
                <h3 className="font-bold text-lg mb-4 text-gray-900 dark:text-white">Reports (Last 7 Days)</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={last7Days}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#10b981" />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </div>

            {/* Additional Insights */}
            <div className="grid lg:grid-cols-3 gap-6 mb-6">
              {/* Top Categories */}
              <Card className="p-6 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm">
                <h3 className="font-bold text-lg mb-4 text-gray-900 dark:text-white">Top Categories</h3>
                <div className="space-y-3">
                  {categoryData.slice(0, 5).map((item, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <span className="text-sm text-gray-700 dark:text-gray-200 capitalize">{item.category}</span>
                      <Badge variant="outline" className="bg-emerald-50 dark:bg-emerald-900/20">
                        {item.count}
                      </Badge>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Top Neighborhoods */}
              <Card className="p-6 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm">
                <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  <span className="text-gray-900 dark:text-white">Top Neighborhoods</span>
                </h3>
                <div className="space-y-3">
                  {topNeighborhoods.map((item, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <span className="text-sm text-gray-700 dark:text-gray-200">{item.name}</span>
                      <Badge variant="outline" className="bg-blue-50 dark:bg-blue-900/20">
                        {item.count}
                      </Badge>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Performance Metrics */}
              <Card className="p-6 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm">
                <h3 className="font-bold text-lg mb-4 text-gray-900 dark:text-white">Performance</h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">Avg Resolution Time</p>
                    <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                      {avgResolutionTime} days
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">Resolution Rate</p>
                    <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {stats.total > 0 ? ((stats.resolved / stats.total) * 100).toFixed(0) : 0}%
                    </p>
                  </div>
                </div>
              </Card>
            </div>

            {/* High Priority Reports */}
            <Card className="p-6 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-lg flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                  <span className="text-gray-900 dark:text-white">High Priority Reports</span>
                </h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setViewMode("table")}
                >
                  View All
                </Button>
              </div>
              <div className="space-y-3">
                {highPriorityReports.length > 0 ? (
                  highPriorityReports.map((report) => (
                    <div
                      key={report.id}
                      className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg cursor-pointer hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                      onClick={() => navigate(createPageUrl(`ReportDetails?id=${report.id}`))}
                    >
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 dark:text-white">{report.title}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-300">{report.neighborhood}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
                          <TrendingUp className="w-3 h-3 mr-1" />
                          {report.upvote_count} upvotes
                        </Badge>
                        <Badge variant="outline">
                          {report.status.replace('_', ' ')}
                        </Badge>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-gray-500 dark:text-gray-400 py-4">
                    No high priority reports at the moment
                  </p>
                )}
              </div>
            </Card>
          </>
        ) : (
          /* Table View */
          <Card className="p-6 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm">
            <h3 className="font-bold text-lg mb-4 text-gray-900 dark:text-white">All Reports</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b dark:border-gray-700">
                    <th className="text-left p-3 text-sm font-semibold text-gray-700 dark:text-gray-200">Title</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700 dark:text-gray-200">Category</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700 dark:text-gray-200">Location</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700 dark:text-gray-200">Status</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700 dark:text-gray-200">Priority</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700 dark:text-gray-200">Date</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700 dark:text-gray-200">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((report) => {
                    const StatusIcon = statusColors[report.status]?.icon || AlertCircle;
                    return (
                      <tr 
                        key={report.id} 
                        className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                        onClick={() => navigate(createPageUrl(`ReportDetails?id=${report.id}`))}
                      >
                        <td className="p-3 text-sm text-gray-900 dark:text-white">{report.title}</td>
                        <td className="p-3 text-sm text-gray-600 dark:text-gray-300 capitalize">
                          {report.category.replace('_', ' ')}
                        </td>
                        <td className="p-3 text-sm text-gray-600 dark:text-gray-300">{report.neighborhood || 'Unknown'}</td>
                        <td className="p-3">
                          <Badge className={`${statusColors[report.status]?.bg} ${statusColors[report.status]?.text} text-xs`}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {report.status.replace('_', ' ')}
                          </Badge>
                        </td>
                        <td className="p-3">
                          <Badge variant="outline" className="text-xs">
                            {report.upvote_count || 0} upvotes
                          </Badge>
                        </td>
                        <td className="p-3 text-sm text-gray-600 dark:text-gray-300">
                          {format(new Date(report.created_date), 'MMM d, yyyy')}
                        </td>
                        <td className="p-3" onClick={(e) => e.stopPropagation()}>
                          {report.status === 'reported' && !report.assigned_to && (
                            <Dialog onOpenChange={(open) => !open && setContractorEmail("")}>
                              <DialogTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setAssigningReport(report)}
                                >
                                  Assign
                                </Button>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle>Assign Contractor</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4">
                                  <div>
                                    <Label htmlFor="contractor-email">Contractor Email</Label>
                                    <Input
                                      id="contractor-email"
                                      placeholder="contractor@example.com"
                                      value={contractorEmail}
                                      onChange={(e) => setContractorEmail(e.target.value)}
                                    />
                                  </div>
                                  <Button
                                    onClick={() => assignContractorMutation.mutate({
                                      reportId: report.id,
                                      email: contractorEmail,
                                      paymentAmount: 300
                                    })}
                                    disabled={!contractorEmail || assignContractorMutation.isPending}
                                    className="w-full"
                                  >
                                    {assignContractorMutation.isPending ? "Assigning..." : "Assign Contractor"}
                                  </Button>
                                </div>
                              </DialogContent>
                            </Dialog>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
